import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Result, Button } from 'antd';
import styled from 'styled-components';
import { useUserStore } from 'stores';
import { recordingsAPI } from 'utils/portal-apis';
import CenteredLoading from 'components/CenteredLoading';

const ErrorContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  background: #0a0a0a;
`;

/**
 * RecordingRoute - Route guard for recording pages with ownership validation
 *
 * Features:
 * - Validates user is authenticated (via PrivateRoute wrapper)
 * - Validates recording exists
 * - Validates user owns the recording (user_id matches)
 * - Shows loading state while validating
 * - Shows error state if validation fails
 * - Redirects to dashboard if unauthorized
 *
 * Usage:
 * <RecordingRoute>
 *   <RecordingPage />
 * </RecordingRoute>
 */
function RecordingRoute({ children }) {
  const { recordingId } = useParams();
  const user = useUserStore(state => state.user);
  const [isValidating, setIsValidating] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthorized, setIsAuthorized] = useState(false);

  useEffect(() => {
    validateAccess();
  }, [recordingId, user]);

  /**
   * Validate user has access to this recording
   */
  const validateAccess = async () => {
    if (!recordingId || recordingId === 'undefined') {
      setError('Recording ID is required');
      setIsValidating(false);
      return;
    }

    if (!user?._id) {
      setError('User not authenticated');
      setIsValidating(false);
      return;
    }

    setIsValidating(true);

    try {
      // Fetch recording from API (backend validates ownership including cohosts)
      const recording = await recordingsAPI.getRecording(recordingId);

      // Check if user is owner, cohost, host in composition, or admin
      const isOwner = recording.user_id === user._id;
      const isCohost = recording.cohosts?.some(cohost => cohost.user_id === user._id);
      const isHostInComposition = recording.current_composition?.hosts?.some(host => host.user_id === user._id);
      const isAdmin = user.role === 'admin';

      if (isOwner || isCohost || isHostInComposition || isAdmin) {
        setIsAuthorized(true);
      } else {
        setError('You do not have permission to access this recording');
        setIsAuthorized(false);
      }
    } catch (error) {
      console.error('Failed to validate recording access:', error);

      // Check if 404 or 403
      if (error.response?.status === 404) {
        setError('Recording not found');
      } else if (error.response?.status === 403) {
        setError('You do not have permission to access this recording');
      } else {
        setError('Failed to load recording');
      }

      setIsAuthorized(false);
    } finally {
      setIsValidating(false);
    }
  };

  // Show loading state
  if (isValidating) {
    return <CenteredLoading tip="Loading recording..." />;
  }

  // Show error state
  if (error || !isAuthorized) {
    return (
      <ErrorContainer>
        <Result
          status="403"
          title="Access Denied"
          subTitle={error || 'You do not have permission to access this recording'}
          extra={
            <Button type="primary" href="/broadcast/my">
              Back to Dashboard
            </Button>
          }
        />
      </ErrorContainer>
    );
  }

  // Render children if authorized
  return <>{children}</>;
}

export default RecordingRoute;
